const API_BASE = process.env.REACT_APP_API_URL || '';

class InvoiceService {
  async getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string'
            ? errorData.detail
            : errorData.detail.map(err => err.msg).join(', ');
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  async getInvoices(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.facility_id) queryParams.append('facility_id', params.facility_id);
    if (params.skip !== undefined) queryParams.append('skip', params.skip);
    if (params.limit !== undefined) queryParams.append('limit', params.limit);

    const url = `${API_BASE}/api/invoices${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    });
    return await this.handleResponse(response);
  }

  async exportCsv(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.facility_id) queryParams.append('facility_id', params.facility_id);

    const url = `${API_BASE}/api/invoices/export/csv${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const token = localStorage.getItem('access_token');
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'rechnungen.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=(.+)/);
      if (match) filename = match[1];
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  }

  async downloadPdf(invoiceId) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/pdf`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  formatDate(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getPaymentMethodLabel(method) {
    const methodMap = {
      'cash': 'Bar',
      'card': 'Karte',
      'invoice': 'Rechnung'
    };
    return methodMap[method] || method || '-';
  }

  async getDailyReport(reportDate, facilityId) {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({
      date: reportDate,
      facility_id: facilityId
    });
    const response = await fetch(`${API_BASE}/api/reports/daily?${params}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tagesabschluss_${reportDate}_${facilityId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getCashbookDailySummary(facilityId, targetDate) {
    const params = new URLSearchParams({ facility_id: facilityId });
    if (targetDate) params.append('target_date', targetDate);
    const url = `${API_BASE}/api/cashbook/daily-summary?${params}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    });
    return await this.handleResponse(response);
  }

  async getDailyReportCsv(reportDate, facilityId) {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({
      date: reportDate,
      facility_id: facilityId
    });
    const response = await fetch(`${API_BASE}/api/reports/daily/csv?${params}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tagesabschluss_${reportDate}_${facilityId}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export default new InvoiceService();
